import { ICatchHubLogFilter, ICatchHubLogModel } from './../models/catchHubLog/catchHubLog.model';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Helper } from './helper.service';
import { catchError, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CatchHubLogsService {
  constructor(
    private http: HttpClient,
    private helperService: Helper,
  ) {}

  public addCatchHubLogs(logs: ICatchHubLogModel[]): Observable<any> {
    return this.http
      .post(`${environment.catchHubLogServiceEndPoint}/addLogs`, JSON.stringify(logs), { headers: this.helperService.getApiAuthHeader() })
      .pipe(catchError((error) => this.helperService.handleError(error)));
  }

  /**
   * The same write, without the shared error handler's toast. A log row is a side effect of something
   * the user has already been told about, so a failed write must not show them a second failure.
   */
  public addCatchHubLogsQuietly(logs: ICatchHubLogModel[]): Observable<any> {
    return this.http.post(`${environment.catchHubLogServiceEndPoint}/addLogs`, JSON.stringify(logs), { headers: this.helperService.getApiAuthHeader() });
  }

  public getCatchHubLogs(pageSize: Number, pageNumber: Number, orgId?: string, filter?: ICatchHubLogFilter): Observable<any> {
    let url = `${environment.catchHubLogServiceEndPoint}/getlogs?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    url += this.buildFilterQuery(orgId, filter);
    return this.http.get(url, { headers: this.helperService.getApiAuthHeader() }).pipe(catchError((error) => this.helperService.handleError(error)));
  }

  public getCatchHubLogCount(orgId?: string, filter?: ICatchHubLogFilter): Observable<any> {
    let url = `${environment.catchHubLogServiceEndPoint}/getTotalLogsCount`;
    const query = this.buildFilterQuery(orgId, filter);
    if (query) {
      url += `?${query.startsWith('&') ? query.slice(1) : query}`;
    }
    return this.http.get(url, { headers: this.helperService.getApiAuthHeader() }).pipe(catchError((error) => this.helperService.handleError(error)));
  }

  public exportCatchHubLogs(orgId?: string, filter?: ICatchHubLogFilter): Observable<HttpResponse<Blob>> {
    let url = `${environment.catchHubLogServiceEndPoint}/export`;
    const query = this.buildFilterQuery(orgId, filter);
    if (query) {
      url += `?${query.startsWith('&') ? query.slice(1) : query}`;
    }
    return this.http
      .get(url, { headers: this.helperService.getApiAuthHeader(), responseType: 'blob', observe: 'response' })
      .pipe(catchError((error) => this.helperService.handleError(error)));
  }

  public getEventTypes(orgId?: string): Observable<string[]> {
    let url = `${environment.catchHubLogServiceEndPoint}/eventTypes`;
    if (orgId) {
      url += `?organizationId=${encodeURIComponent(orgId)}`;
    }
    return this.http
      .get<string[]>(url, { headers: this.helperService.getApiAuthHeader() })
      .pipe(catchError((error) => this.helperService.handleError(error)));
  }

  /** Deletes the logs of a single organisation — `orgId` is honoured for sysadmins, everyone else's own org. */
  public deleteOrgLogs(orgId?: string): Observable<any> {
    let url = `${environment.catchHubLogServiceEndPoint}/deleteOrgLogs`;
    if (orgId) {
      url += `?organizationId=${encodeURIComponent(orgId)}`;
    }
    return this.http.delete(url, { headers: this.helperService.getApiAuthHeader() }).pipe(catchError((error) => this.helperService.handleError(error)));
  }

  /** Builds the shared `&key=value` query fragment (leading `&`) for org + filter fields. */
  private buildFilterQuery(orgId?: string, filter?: ICatchHubLogFilter): string {
    const params: string[] = [];
    if (orgId) {
      params.push(`organizationId=${encodeURIComponent(orgId)}`);
    }
    if (filter?.searchDescription) {
      params.push(`searchDescription=${encodeURIComponent(filter.searchDescription)}`);
    }
    if (filter?.fromDate) {
      params.push(`fromDate=${encodeURIComponent(filter.fromDate)}`);
    }
    if (filter?.toDate) {
      params.push(`toDate=${encodeURIComponent(filter.toDate)}`);
    }
    if (filter?.logType) {
      params.push(`logType=${encodeURIComponent(filter.logType)}`);
    }
    if (filter?.eventType) {
      params.push(`eventType=${encodeURIComponent(filter.eventType)}`);
    }
    if (filter?.userName) {
      params.push(`userName=${encodeURIComponent(filter.userName)}`);
    }
    return params.length ? `&${params.join('&')}` : '';
  }
}
